import { ref, watch, inject } from "vue";
import { useStore } from "vuex";
import { extractErrorMessages } from "@/helpers/apiErrorHelper";

export default function useImportRangePinsModal(props, emit) {
  const store = useStore();

  const targetCourse = inject("targetCourse", null);

  const selectedFile = ref(null);
  const fileName = ref("");
  const isLoading = ref(false);
  const errorMessage = ref("");
  const errorMessages = ref([]);

  const resetForm = () => {
    selectedFile.value = null;
    fileName.value = "";
    errorMessage.value = "";
    errorMessages.value = [];
  };

  const isCsvFile = (file) => {
    // Windows reports .csv as an Excel type, so the extension is the reliable
    // signal. Names arrive in any case, hence the lowercasing.
    return file.name.toLowerCase().endsWith(".csv");
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    // Clearing the input means picking the same file again still fires a
    // change event, so a rejected file can be re-selected after renaming it.
    event.target.value = "";

    errorMessages.value = [];

    if (!file) {
      selectedFile.value = null;
      fileName.value = "";
      return;
    }

    if (!isCsvFile(file)) {
      errorMessage.value = "Please select a CSV file";
      selectedFile.value = null;
      fileName.value = "";
      return;
    }

    selectedFile.value = file;
    fileName.value = file.name;
    errorMessage.value = "";
  };

  const handleImport = async () => {
    if (!selectedFile.value) {
      errorMessage.value = "Please select a CSV file";
      return;
    }

    if (!targetCourse.value) {
      errorMessage.value = "Please select a course first";
      return;
    }

    isLoading.value = true;
    errorMessage.value = "";
    errorMessages.value = [];

    try {
      const result = await store.dispatch(
        "drivingRangePins/importDrivingRangePins",
        {
          courseId: targetCourse.value.id,
          file: selectedFile.value,
        }
      );

      store.dispatch("toastNotification/displayToast", {
        message: `${
          result?.message || "Driving range pins imported successfully."
        }`,
        type: "success",
      });

      isLoading.value = false;
      closeModal();
    } catch (error) {
      isLoading.value = false;

      const messages = extractErrorMessages(
        error,
        "An unknown error occurred. Please try again."
      );

      // A single failure reads better as one line than as a one-item list.
      if (messages.length === 1) {
        errorMessage.value = messages[0];
      } else {
        errorMessages.value = messages;
      }
    }
  };

  const closeModal = () => {
    resetForm();
    emit("update:modelValue", false);
  };

  watch(
    () => props.modelValue,
    (newVal) => {
      if (newVal === true) {
        resetForm();
      }
    }
  );

  return {
    targetCourse,
    selectedFile,
    fileName,
    isLoading,
    errorMessage,
    errorMessages,
    handleFileChange,
    handleImport,
    closeModal,
  };
}
